import React, { useState, useRef } from 'react';
import axios from 'axios';
import { X, UploadSimple, FilePdf, CheckCircle, WarningCircle, ArrowRight } from '@phosphor-icons/react';
import { groupSelections } from '../lib/bundleHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// One row in the preview's "Proposed Bundles" list. Grouped flavours like saved bundles.
const ProposedBundleRow = ({ bundle, idx }) => {
  const grouped = groupSelections(bundle.products);
  return (
    <details className="bg-black/40 border border-border-color rounded-lg overflow-hidden">
      <summary className="cursor-pointer list-none p-3 flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-mono text-gold">#{idx + 1}</span>
            <span className="text-text-primary font-semibold">
              {bundle.proteinSize} {bundle.kind?.endsWith('box') ? 'box' : 'single'}
            </span>
            <span className="text-text-secondary text-xs">({bundle.products.length} items)</span>
          </div>
          <p className="text-xs text-text-secondary mt-0.5">
            RRP <span className="text-text-primary">${bundle.totalRrp?.toFixed(2)}</span>
            <span className="mx-1">·</span>
            Saving <span className="text-success">${bundle.customerSaving?.toFixed(2)} ({bundle.discountPct?.toFixed(0)}%)</span>
            <span className="mx-1">·</span>
            Total <span className="text-gold font-semibold">${bundle.bundleRrp?.toFixed(2)}</span>
          </p>
        </div>
        <span className="text-text-secondary text-xs shrink-0">▾</span>
      </summary>
      <div className="px-3 pb-3 pt-1 border-t border-border-color text-xs space-y-0.5">
        {grouped.map((g) => (
          <div key={`${g.id}-${g.selectedFlavour || 'def'}`} className="flex justify-between text-text-primary">
            <span className="truncate pr-2">
              {g.name}
              {g.selectedFlavour && <span className="text-gold"> – {g.selectedFlavour}</span>}
              {g.qty > 1 && <span className="ml-1 text-gold font-semibold">× {g.qty}</span>}
            </span>
            <span className="text-text-secondary shrink-0">${(g.rrp * g.qty).toFixed(2)}</span>
          </div>
        ))}
      </div>
    </details>
  );
};

const LeftoversList = ({ leftovers }) => {
  if (!leftovers?.length) return null;
  return (
    <div className="space-y-1 text-xs">
      {leftovers.map((l, i) => (
        <div key={`${l.productId}-${l.flavour || 'd'}-${i}`} className="flex justify-between text-text-primary">
          <span className="truncate pr-2">
            {l.name}
            {l.flavour && <span className="text-gold"> – {l.flavour}</span>}
            <span className="ml-1 text-text-secondary">× {l.qty}</span>
          </span>
          <span className="text-text-secondary shrink-0">${(l.rrp * l.qty).toFixed(2)} RRP</span>
        </div>
      ))}
    </div>
  );
};

const UnmatchedList = ({ unmatched }) => {
  if (!unmatched?.length) return null;
  return (
    <div className="space-y-1 text-xs">
      {unmatched.map((u) => (
        <div key={u.sku + u.description} className="flex justify-between text-text-secondary">
          <span className="truncate pr-2">
            <span className="font-mono text-text-secondary">{u.sku}</span>
            <span className="mx-1">·</span>
            {u.description}
            <span className="ml-1 text-text-secondary">× {u.qty}</span>
          </span>
          <span className="text-text-secondary shrink-0">${u.line_total?.toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
};

export const ImportOrderPdfModal = ({ open, onClose, customerId, onApplied }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    setLoading(false);
    setApplying(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!open) return null;

  const handleFile = (f) => {
    setError(null);
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) {
      setError('Only .pdf files are supported.');
      return;
    }
    setFile(f);
    setPreview(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    if (!customerId) {
      setError('Pick a customer first (Building for) before importing.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/orders/import-pdf`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!preview?.bundles?.length) return;
    setApplying(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/orders/import-pdf/apply`, {
        customerId,
        order_number: preview.order_number,
        bundles: preview.bundles,
      });
      onApplied?.(res.data);
      handleClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="import-pdf-modal">
      <div className="bg-surface border border-border-color rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-color shrink-0">
          <div className="flex items-center gap-3">
            <FilePdf size={24} weight="duotone" className="text-gold" />
            <h2 className="text-xl font-bold font-heading text-text-primary">Import Order PDF</h2>
          </div>
          <button onClick={handleClose} data-testid="close-import-modal" className="text-text-secondary hover:text-text-primary" aria-label="Close">
            <X size={22} weight="bold" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-4">
          {!preview && (
            <>
              <p className="text-sm text-text-secondary">
                Upload a Raisey's draft-order PDF. We'll match SKUs to PRO75 products and propose the
                saver combination that maximises the customer's discount.
              </p>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
                }}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${dragOver ? 'border-gold bg-gold/5' : 'border-border-color hover:border-gold/50'}`}
                data-testid="import-pdf-dropzone"
              >
                <UploadSimple size={36} weight="duotone" className="text-gold mx-auto mb-2" />
                <p className="text-sm text-text-primary">
                  {file ? <span className="font-semibold text-gold">{file.name}</span> : 'Click or drag a PDF here'}
                </p>
                <p className="text-xs text-text-secondary mt-1">PDF only · Max 10MB recommended</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="import-pdf-file-input"
                  className="hidden"
                />
              </div>
              {file && (
                <button
                  onClick={handleUpload}
                  disabled={loading || !customerId}
                  data-testid="import-pdf-analyse-button"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gold text-black font-bold rounded-lg hover:bg-gold-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Analysing…' : 'Analyse PDF'}
                  {!loading && <ArrowRight size={18} weight="bold" />}
                </button>
              )}
              {!customerId && (
                <p className="text-xs text-warning">Pick a customer in the "Building for" dropdown before analysing.</p>
              )}
            </>
          )}

          {preview && (
            <>
              <div className="bg-black/40 border border-border-color rounded-xl p-4" data-testid="import-pdf-summary">
                <p className="text-sm text-text-secondary">
                  Order <span className="font-mono text-gold">{preview.order_number || 'unknown'}</span>
                </p>
                <div className="grid grid-cols-3 gap-3 mt-3 text-sm">
                  <div>
                    <p className="text-xs text-text-secondary">Saver RRP</p>
                    <p className="text-text-primary font-semibold">${preview.totals.total_rrp.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-secondary">Total Discount</p>
                    <p className="text-success font-semibold" data-testid="import-pdf-saving">
                      ${preview.totals.total_discount.toFixed(2)} ({preview.totals.total_discount_pct.toFixed(0)}%)
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-text-secondary">Discounted Total</p>
                    <p className="text-gold font-bold">${preview.totals.discounted_total.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={16} weight="fill" className="text-success" />
                  <h3 className="text-sm font-semibold text-text-primary" data-testid="import-pdf-bundles-heading">
                    Proposed Savers ({preview.bundles.length})
                  </h3>
                </div>
                {preview.bundles.length === 0 ? (
                  <p className="text-xs text-text-secondary italic">No savers could be formed from the matched items.</p>
                ) : (
                  <div className="space-y-2">
                    {preview.bundles.map((b, i) => (
                      <ProposedBundleRow key={b.tempId} bundle={b} idx={i} />
                    ))}
                  </div>
                )}
              </div>

              {preview.leftovers?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <WarningCircle size={16} weight="fill" className="text-warning" />
                    <h3 className="text-sm font-semibold text-text-primary">
                      Unbundled items <span className="text-text-secondary font-normal">— matched products that didn&apos;t fit a saver</span>
                    </h3>
                  </div>
                  <LeftoversList leftovers={preview.leftovers} />
                </div>
              )}

              {preview.unmatched_lines?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <WarningCircle size={16} weight="fill" className="text-text-secondary" />
                    <h3 className="text-sm font-semibold text-text-primary">
                      Not in PRO75 catalog <span className="text-text-secondary font-normal">— skipped (no SKU match)</span>
                    </h3>
                  </div>
                  <UnmatchedList unmatched={preview.unmatched_lines} />
                </div>
              )}
            </>
          )}

          {error && (
            <div className="text-xs px-3 py-2 rounded text-danger bg-danger/10 border border-danger/30" data-testid="import-pdf-error">
              {error}
            </div>
          )}
        </div>

        {preview && (
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border-color shrink-0">
            <button
              onClick={reset}
              data-testid="import-pdf-back-button"
              className="text-xs text-text-secondary hover:text-text-primary"
            >
              ← Upload a different PDF
            </button>
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary"
                data-testid="import-pdf-cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                disabled={applying || preview.bundles.length === 0}
                data-testid="import-pdf-apply-button"
                className="px-4 py-2 bg-gold text-black text-sm font-bold rounded-lg hover:bg-gold-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {applying ? 'Applying…' : `Apply ${preview.bundles.length} saver${preview.bundles.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportOrderPdfModal;
